// Thin client for the MSync API (the .NET 10 vertical slice).
// ?? not ||: deployed builds set VITE_API_BASE="" (same-origin — the API serves the SPA), and "" is
// falsy, so || would silently send a deployed app's requests to localhost.
const BASE = import.meta.env.VITE_API_BASE ?? "http://localhost:5137";

// Bearer-token provider. Registered by the auth bootstrap (src/auth.jsx) when Entra sign-in is active;
// stays null in local dev, where the API's dev-stub authenticates every request.
let _getToken = null;
export function setTokenProvider(fn) { _getToken = fn; }
async function authHeaders() {
  if (!_getToken) return {};
  const t = await _getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ---- Read cache. Every screen mount refetches its world (managers, properties, sensors, the billing
// preview...), so hash navigation used to refire a dozen identical requests — including the billing
// engine — per click. Reads share one in-flight promise and live for a short TTL; ANY mutation clears
// the whole cache, so a write is always followed by fresh reads. Per page-load only (module state).
const READ_TTL_MS = 30_000;
const _reads = new Map();   // key -> { at, promise }
function cachedRead(key, fn) {
  const hit = _reads.get(key);
  if (hit && Date.now() - hit.at < READ_TTL_MS) return hit.promise;
  let promise;
  promise = fn().catch((e) => {
    // Failures don't poison the TTL — but only evict OUR OWN entry: a slow stale request rejecting
    // late must not delete a fresh entry that superseded it (that caused duplicate refetches).
    if (_reads.get(key)?.promise === promise) _reads.delete(key);
    throw e;
  });
  _reads.set(key, { at: Date.now(), promise });
  return promise;
}
// Exported: screens that KNOW server state just changed underneath them (e.g. the Monnit sync job
// finishing minutes after its enqueue mutation cleared the cache) call this before refetching.
export function clearReadCache() { _reads.clear(); }

async function rawGet(path) {
  const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json();
}
const get = (path) => cachedRead(`GET ${path}`, () => rawGet(path));

async function rawPost(path, body) {
  const res = await fetch(`${BASE}${path}`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json();
}
// POSTs are mutations: clear cached reads so what follows refetches truth. Cleared on BOTH sides of
// the request — a read landing mid-mutation would otherwise re-cache pre-mutation data that survives it.
async function post(path, body) { clearReadCache(); try { return await rawPost(path, body); } finally { clearReadCache(); } }
// The billing-engine preview is a read shaped as a POST — cache it like a GET.
const postRead = (path, body) => cachedRead(`POST ${path} ${JSON.stringify(body ?? {})}`, () => rawPost(path, body));

async function put(path, body) {
  clearReadCache();
  try {
    const res = await fetch(`${BASE}${path}`, { method: "PUT", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(`${path} -> ${res.status}`);
    return await res.json();
  } finally { clearReadCache(); }
}

async function del(path) {
  clearReadCache();
  try {
    const res = await fetch(`${BASE}${path}`, { method: "DELETE", headers: await authHeaders() });
    if (!res.ok) {
      const e = new Error(`${path} -> ${res.status}`);
      e.status = res.status;
      try { e.body = await res.json(); } catch { /* no body */ }
      throw e;
    }
    return res.status === 204 ? null : await res.json().catch(() => null);
  } finally { clearReadCache(); }
}

// POST with no expected JSON body (e.g. 204 No Content endpoints).
async function postVoid(path, body) {
  clearReadCache();
  try {
    const res = await fetch(`${BASE}${path}`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify(body ?? {}) });
    if (!res.ok) throw new Error(`${path} -> ${res.status}`);
    return res.status === 204 ? null : await res.json().catch(() => null);
  } finally { clearReadCache(); }
}

// Auth bootstrap + identity (see src/auth.jsx). auth-config is anonymous; me requires a token.
export const fetchAuthConfig = () => get("/api/auth-config");
export const fetchMe = () => get("/api/me");

export const fetchUnits = () => get("/api/units");
// Usage over time for a scope. granularity: "hour" | "day" | "month". offset = windows back from now (0 = latest).
export const fetchUsage = ({ managerId, realmId, propertyId, tenancyId, granularity, points, offset } = {}) => {
  const q = new URLSearchParams();
  if (managerId) q.set("managerId", managerId);
  if (realmId) q.set("realmId", realmId);
  if (propertyId) q.set("propertyId", propertyId);
  if (tenancyId) q.set("tenancyId", tenancyId);
  if (granularity) q.set("granularity", granularity);
  if (points) q.set("points", points);
  if (offset) q.set("offset", offset);
  const s = q.toString();
  return get("/api/usage" + (s ? `?${s}` : ""));
};
export const fetchProperties = () => get("/api/properties");
export const fetchManagers = () => get("/api/managers");
export const fetchUnitDetail = (id) => get(`/api/units/${id}`);
export const updateUnit = (tenancyId, fields) => put(`/api/units/${tenancyId}`, fields);   // edit tenancy settings
export const createUnit = (fields) => post("/api/units", fields);            // new unit + first tenancy
export const deleteUnit = (tenancyId) => del(`/api/units/${tenancyId}`);     // 204 · 409 if it has history
export const createProperty = (realmId, name) => post("/api/properties", { realmId, name });
export const updateProperty = (id, name) => put(`/api/properties/${id}`, { name });
export const deleteProperty = (id) => del(`/api/properties/${id}`);           // 204 · 409 if it has units
export const fetchSensors = (managerId) => get("/api/sensors" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""));
export const fetchSensorDetail = (id) => get(`/api/sensors/${id}`);
export const fetchTemplates = () => get("/api/templates");
// Retire the unit's active meter and attach a new one. rec: { sensorId, counterIndex, scale, effectiveFrom, label? }
export function swapMeter(tenancyId, rec) {
  return post(`/api/units/${tenancyId}/swap-meter`, {
    sensorId: rec.sensorId, counterIndex: rec.counterIndex, scale: rec.scale,
    effectiveFrom: rec.effectiveFrom, label: rec.label || null,
  });
}

// Persist a template edit (term, memos, lines). Returns the reloaded template.
export function saveTemplate(id, { term, memo, autoPayMemo, lines }) {
  return put(`/api/templates/${id}`, {
    term, memo, autoPayMemo,
    lines: (lines || []).map((l) => ({ order: +l.order, fixed: !!l.fixed, desc: l.desc, price: +l.price, account: l.account || "Utilities", itemId: l.itemId || null })),
  });
}

// Create a new template (invoice definition) with lines. Returns the created template.
export function createTemplate({ name, term, frequencyMonths, memo, autoPayMemo, realmId, lines }) {
  return post("/api/templates", {
    name, term: term || "", frequencyMonths: +frequencyMonths || 1, memo: memo || "", autoPayMemo: autoPayMemo || null, realmId: realmId || null,
    lines: (lines || []).map((l) => ({ order: +l.order, fixed: !!l.fixed, desc: l.desc, price: +l.price, account: l.account || "Utilities", itemId: l.itemId || null })),
  });
}
export const deleteTemplate = (id) => del(`/api/templates/${id}`);   // 204 ok · 409 if in use · 404 if missing

// Map an /api/templates row into the Templates & Rates card/editor shape.
export function mapTemplate(t) {
  return {
    id: t.id, name: t.name, term: t.term, frequencyMonths: t.frequencyMonths,
    managerId: t.managerId, managerName: t.managerName, usedBy: t.usedBy,
    memo: t.memo, autoPayMemo: t.autoPayMemo, realmId: t.realmId,
    lines: (t.lines || []).map((l) => ({ order: l.order, fixed: l.fixed, desc: l.desc, price: l.price, account: l.account, itemId: l.itemId })),
  };
}
export const fetchInvoices = (managerId) => get("/api/invoices" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""));
export const fetchInvoiceDetail = (id) => get(`/api/invoices/${id}`);
// R7: pull payment status (Paid / Overdue / Void) from QuickBooks for the scope's pushed invoices.
// -> { synced, realms, checked, updated }; blocked / unconnected realms are skipped server-side.
// Single-flight with a 60s window: the Invoices screen used to fire this several times per visit
// (mount storms + scope changes), and each call is now a full-book QBO poll. Only a refresh that
// actually flipped statuses invalidates cached reads.
let _payRefresh = { key: null, at: 0, promise: null };
export function refreshInvoicePayments(managerId) {
  const key = managerId || "";
  const entry = _payRefresh;
  if (entry.promise && entry.key === key && Date.now() - entry.at < 60_000)
    // Replayed from the single-flight window: same result, but firstDelivery=false so remounts
    // within the window don't re-toast an already-announced update.
    return entry.promise.then((r) => ({ ...r, firstDelivery: false }));
  let promise;
  promise = rawPost("/api/invoices/payments/refresh" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""), {})
    .then((r) => { if (r && r.updated > 0) clearReadCache(); return r; })
    .catch((e) => {
      if (_payRefresh.promise === promise) _payRefresh.promise = null;   // only evict our own entry
      throw e;
    });
  _payRefresh = { key, at: Date.now(), promise };
  return promise.then((r) => ({ ...r, firstDelivery: true }));
}
export const fetchBillPreview = (managerId, readDate, realmId) => postRead("/api/invoice-runs/preview", { managerId: managerId || null, realmId: realmId || null, readDate });
export const commitBillRun = (readDate, items) => post("/api/invoice-runs/commit", { readDate, items });
export const fetchLoans = (managerId) => get("/api/loans" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""));
export const previewLoanBills = (managerId, date) => postRead("/api/loans/preview", { managerId: managerId || null, date });   // amortized monthly bills — a READ shaped as a POST, cached like one
export const fetchLoanBalances = (managerId) => get("/api/loans/balances" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""));   // live QBO balance (allow-listed realms only)
export const updateLoan = (id, fields) => put(`/api/loans/${id}`, fields);   // edit loan terms + splits
export const createLoan = (realmId, loan) => post("/api/loans", { realmId, loan });
export const deleteLoan = (id) => del(`/api/loans/${id}`);
export const createLoanBill = (id, date) => post(`/api/loans/${id}/create-bill`, { date });   // per-loan QBO Vendor Bill (dry-run until push is on)
export const fetchAccountOrigination = (realmId, acctId) => get(`/api/qbo/realms/${encodeURIComponent(realmId)}/accounts/${encodeURIComponent(acctId)}/origination`);   // start month + opening/current balance from the account's GL
export const fetchLoanSchedule = (id) => get(`/api/loans/${id}/schedule`);   // month-by-month amortization
export const syncMonnit = () => post("/api/monnit/sync", {});   // enqueues the Hangfire ingestion job
export const fetchMonnitStatus = () => rawGet("/api/monnit/status");   // POLLED for live sync progress — must bypass the read cache
export const fetchQboStatus = () => get("/api/qbo/status");            // { environment, pushEnabled, configured, emailEnabled, note }
export const prepareQboInvoice = (id) => postRead(`/api/qbo/invoices/${id}/prepare`, {});   // dry-run QBO payload — read-only preview, cached like one
export const fetchQboConnections = (managerId) => get("/api/qbo/connections" + (managerId ? `?managerId=${encodeURIComponent(managerId)}` : ""));   // companies in scope (optionally one manager's)
export const qboConnect = (managerId) => rawGet(`/api/qbo/connect?managerId=${encodeURIComponent(managerId)}`);   // { url, configured } — fresh OAuth state every click, never cached
export const qboDisconnect = (realmId) => postVoid(`/api/qbo/realms/${realmId}/disconnect`);  // 204
export const renameQboCompany = (realmId, name) => put(`/api/qbo/realms/${realmId}/name`, { name });  // manual company label
export const sendQboInvoice = (id, email = false) => post(`/api/qbo/invoices/${id}/send?email=${email ? "true" : "false"}`, {});   // gated real send (dry run until push on) · ?email also asks Intuit to email it
export const qboEmailInvoice = (id) => post(`/api/qbo/invoices/${id}/email`, {});        // email an already-pushed invoice (config-gated)
export const fetchQboRef = (realmId, type) => get(`/api/qbo/realms/${realmId}/ref?type=${encodeURIComponent(type)}`);   // cached picker list
export const fetchQboRefCounts = (realmId) => get(`/api/qbo/realms/${realmId}/ref-counts`);   // { Customer: n, Item: n, ... }
export const refreshQboRef = (realmId) => post(`/api/qbo/realms/${realmId}/ref/refresh`, {}); // pull lists from QuickBooks
export const importQboInvoices = (realmId) => post(`/api/qbo/realms/${realmId}/import-invoices`, {}); // backfill historical QBO invoices -> { synced, scanned, imported, skippedExisting, unmatchedCustomer, periodCollisions }
export const fetchQboReconcile = (realmId) => get(`/api/qbo/realms/${realmId}/reconcile`);    // { customers, items, loans } unmapped
export const fetchQboLinkProposals = (realmId) => get(`/api/qbo/realms/${realmId}/link-proposals`);   // unlinked fields + exact-name matches
export const applyQboLinks = (realmId, links) => post(`/api/qbo/realms/${realmId}/link-apply`, { links });  // -> { applied, skipped }

// Save a manual/estimated reading. typeId is the UI read-type: 1 Manual, 4 Estimated, 8 Base.
// `hcf` is the reading in HCF as read off the meter; the API stores it scaled and back-computes the raw dial value.
export function addReading(tenancyId, { date, hcf, typeId, readBy }) {
  const source = typeId === 4 ? 3 : 1;           // 3 = Estimated, 1 = Manual (Base is a manual baseline)
  return post(`/api/units/${tenancyId}/readings`, {
    readAt: date, hcf, source, isBaseline: typeId === 8, readBy: readBy || null,
  });
}
// Correct a mistaken reading: edit its date/value, or remove it. HCF is the meter reading (raw dial back-computed).
export const updateReading = (tenancyId, readingId, { date, hcf }) => put(`/api/units/${tenancyId}/readings/${readingId}`, { readAt: date, hcf });
export const deleteReading = (tenancyId, readingId) => del(`/api/units/${tenancyId}/readings/${readingId}`);   // 204

// 1 = in QuickBooks, unpaid, not yet overdue. Displayed as "Open" — QBO's own label for this state,
// since R7 keeps local statuses mirroring QBO. ("Sent" read as emailed-to-tenant; "Pushed" described
// the transmit plumbing, not the state. Delivery is the separate Emailed/Not-emailed chip.)
const INV_STATUS = { 0: "Draft", 1: "Open", 2: "Paid", 3: "Overdue", 4: "Void" };
const parseTenantName = (raw) => { const p = (raw || "").split(":"); return (p[2] || raw || "—").trim(); };

// Map an /api/invoices row into the Invoices grid shape.
export function mapInvoice(r) {
  return {
    id: r.invoiceId,
    number: r.qboDocNumber || String(r.invoiceId),
    tenant: parseTenantName(r.tenant),
    unitName: r.unitName, propName: r.propName, managerName: r.managerName,
    date: new Date(r.createdUtc),
    periodStart: new Date(r.periodStart), periodEnd: new Date(r.periodEnd),
    usage: r.usageQty, total: r.amount,
    status: INV_STATUS[r.status] || "Draft",
    autoPay: r.autoPay, emailed: r.autoEmail, spike: false,
    emailedUtc: r.emailedUtc ? new Date(r.emailedUtc) : null,   // actual delivery (null = never emailed)
    tenantActive: r.tenantActive !== false,                     // former tenants' history hides by default
  };
}

// Map an /api/invoices/{id} detail into the invoice-drawer shape.
export function mapInvoiceDetail(r) {
  return {
    id: r.invoiceId,
    number: r.qboDocNumber || String(r.invoiceId),
    tenant: parseTenantName(r.tenantRaw), tenantRaw: r.tenantRaw,
    unitName: r.unitName, propName: r.propName, managerName: r.managerName,
    date: new Date(r.createdUtc),
    periodStart: new Date(r.periodStart), periodEnd: new Date(r.periodEnd),
    startRead: r.startScaled, endRead: r.endScaled,   // HCF (scaled) meter reading, not the raw dial index
    usage: r.usageQty, total: r.amount,
    status: INV_STATUS[r.status] || "Draft",
    autoPay: r.autoPay, emailed: r.autoEmail,
    emailedUtc: r.emailedUtc ? new Date(r.emailedUtc) : null,   // when Intuit actually emailed it (null = never)
    qboInvoiceId: r.qboInvoiceId || null,                       // QBO txn id -> "Open in QuickBooks" deep link
    memo: r.memo, term: r.term,
    lines: (r.lines || []).map((l) => ({ desc: l.desc, qty: l.qty, rate: l.rate, amount: l.amount, fixed: l.fixed })),
  };
}

// Map an invoice-run preview row (API shape) into the Invoice Run wizard's row shape.
export function mapBillRow(r) {
  return {
    unit: {
      id: r.tenancyId, name: r.unitName, tenant: r.tenant,
      managerId: r.managerId, managerName: r.managerName,
      autoPay: r.autoPay, autoEmail: r.autoEmail,
      pendingRead: r.currRaw != null ? { raw: r.currRaw, hcf: r.currScaled, spike: false } : null,
      template: { name: r.templateName },
    },
    eligible: r.eligible, pending: r.pending, skip: r.skip, isFinal: r.isFinal,
    prior: r.priorDate ? { date: new Date(r.priorDate), raw: r.priorRaw, hcf: r.priorScaled } : null,
    end: r.currDate ? new Date(r.currDate) : null,   // billing period end (the current reading's date)
    periodDays: r.periodDays, usage: r.usage,
    comp: { total: r.total, lines: (r.lines || []).map((l) => ({ desc: l.desc, qty: l.qty, rate: l.rate, amount: l.amount, fixed: l.fixed })) },
  };
}

// Monnit application IDs that are pulse-counter water meters — the only billable devices.
// Everything else (e.g. App 78 = water-heater / temperature sensors) is hidden from the meters list.
const PULSE_COUNTER_APPS = new Set([47, 90]);
// App 47 pulse counters expose up to 4 counters (index 1-4); App 90 has a single counter (always index 1).
const MULTI_COUNTER_APPS = new Set([47]);
export const isPulseCounterApp = (appId) => PULSE_COUNTER_APPS.has(appId);
export const isMultiCounterApp = (appId) => MULTI_COUNTER_APPS.has(appId);

// Map an API sensor into the Sensors screen's shape. `newestMs` = the freshest last-comm across the
// batch, so "offline" is judged relative to the snapshot's own timeline (not wall-clock).
export function mapSensor(s, newestMs) {
  const battery = s.battery ?? 0;
  const lastComm = s.lastComm ? new Date(s.lastComm) : null;
  const offline = !!(lastComm && newestMs && newestMs - lastComm.getTime() > 48 * 3600 * 1000);
  const lowBatt = battery < 35;
  const unitCount = s.unitCount || 0;
  const retired = unitCount === 0;   // no units attached => spare/retired device; its comms don't feed any bill
  return {
    sensorId: s.sensorId,
    monnitAppId: s.monnitAppId,
    isPulseCounter: PULSE_COUNTER_APPS.has(s.monnitAppId),   // false => water-heater / non-meter type, hidden from the list
    isMultiCounter: MULTI_COUNTER_APPS.has(s.monnitAppId),   // App 47 => counters 1-4; App 90 => single counter (index 1)
    name: s.propName || s.name || `Sensor ${s.sensorId}`,
    propName: s.propName || null,       // the real property (via the attached unit); null when unassigned
    sensorName: s.name || null,         // the Monnit device label (its own name in iMonnit)
    unitNames: s.unitNames || "",       // comma-joined names of the units this meter serves
    battery,
    signal: Math.max(0, Math.min(100, Math.round(s.signal || 0))),   // raw 0-100 signal-quality %; the Signal glyph buckets it into bars
    currentReading: s.currentReading != null ? s.currentReading : 0,
    status: s.status,
    lastComm,
    nextComm: s.nextComm ? new Date(s.nextComm) : null,
    unitIds: Array(unitCount).fill(0),   // screen uses .length only
    counters: [],
    offline,
    lowBatt,
    retired,
    // "Needs attention" only applies to metering sensors — a retired/spare device being offline is expected.
    alertsActive: (offline || lowBatt) && !retired,
  };
}

// Map /api/sensors/{id} detail into the sensor drawer's counters + the units this device meters.
export function mapSensorDetail(d) {
  return {
    sensorId: d.sensorId,
    counters: d.counters || [],
    units: (d.units || []).map((u) => ({
      tenancyId: u.tenancyId,
      name: u.unitName,
      tenant: parseTenantName(u.tenantRaw),
      propName: u.propName,
      counter: u.counterIndex,
      scale: u.scale,
      currentReading: u.currentReading,
      lastReadAt: u.lastReadAt ? new Date(u.lastReadAt) : null,
      usage: u.usage,
    })),
  };
}

// Turn a /api/units/{id} detail into the Meter-Readings log shape (usage = delta between readings).
export function mapReadingLog(detail) {
  if (!detail) return { reads: [], counter: null, scale: null };
  const srcType = { 1: 1, 2: 2, 3: 4 };   // Manual / Calculated / Estimated -> mock typeIds
  const asc = (detail.readings || []).slice().sort((a, b) => new Date(a.readAt) - new Date(b.readAt));
  const reads = asc.map((r, i) => {
    const prev = asc[i - 1];
    const usage = prev ? Math.max(0, Math.round(r.scaled - prev.scaled)) : 0;
    return {
      date: new Date(r.readAt), raw: r.raw, scaled: r.scaled, usage,
      typeId: r.isBaseline ? 8 : (srcType[r.source] || 2),
      readBy: r.readBy || "Automated", invoiced: false, pending: false, spike: false,
    };
  });
  const meters = detail.meters || [];
  const active = meters.find((m) => !m.effectiveTo) || meters[meters.length - 1];
  return { reads, counter: active ? active.counterIndex : null, scale: active ? active.scale : null };
}

// Map an API unit row into the shape the Properties screen renders.
// Fields the v2 slice doesn't compute yet (usage series, projection) degrade gracefully.
export function mapUnit(u) {
  const parts = (u.tenantRaw || "").split(":");
  const tenant = (parts[2] || u.tenantRaw || "—").trim();
  return {
    id: u.id,
    name: u.name,
    tenant,
    tenantRaw: u.tenantRaw,
    propName: u.propName,
    propKey: u.propKey,
    managerId: u.managerId,
    managerName: u.managerName,
    sensorId: u.sensorId,
    active: u.active,
    autoInvoice: u.autoInvoice,
    autoPay: u.autoPay,
    invoiceDay: u.invoiceDay,
    moveInDate: u.moveInDate ? new Date(u.moveInDate) : null,
    moveOutDate: u.moveOutDate ? new Date(u.moveOutDate) : null,
    reads: [],                                                   // no per-reading series in this endpoint yet
    pendingUsage: u.usage != null ? u.usage : 0,                 // real this-period usage (latest - prior reading)
    projected: u.projected,                                      // real projected $ (fixed + usage x rate, from template)
    lastRead: u.lastReadDate ? { date: new Date(u.lastReadDate), raw: u.lastReadValue } : null,
    pendingRead: null,
  };
}
